// ─── Modèle de garde PERSONNALISÉ — modale de configuration ──────
// Grille « cycle de N semaines × 7 jours » : chaque case = P1 ou P2. Le cycle
// est ancré sur la date de départ (J1 = jour de départ choisi lors de l'ajout).
// Enregistrable et réutilisable comme un modèle intégré.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, ChoixPuces } from '@mop/core';
import { GARDE_COL_DEFAUT } from './services/garde';

const GARDE_COL = GARDE_COL_DEFAUT;   // source unique : services/garde
const P = ['parent1', 'parent2'];
const idPerso = () => `perso_${Date.now().toString(36)}`;

export default function FeuilleModelePerso({ visible, modele, parents, onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const nomG = (g) => (g === 'parent1' ? (parents?.parent1?.nom || t('garde.parent1')) : (parents?.parent2?.nom || t('garde.parent2')));
  const gardeCol = { parent1: parents?.couleur1 || GARDE_COL.parent1, parent2: parents?.couleur2 || GARDE_COL.parent2 };

  const [nom, setNom] = useState('');
  const [nbSemaines, setNbSemaines] = useState(2);
  const [pattern, setPattern] = useState([]);   // longueur nbSemaines*7
  const [cale, setCale] = useState(false);      // calé sur les semaines du calendrier (ISO)
  const [semaine1, setSemaine1] = useState('paire');  // S1 = semaines paires | impaires (si calé, nb=2)

  useEffect(() => {
    if (!visible) return;
    const nb = modele?.nbSemaines || 2;
    setNom(modele?.nom || '');
    setNbSemaines(nb);
    setPattern(ajuster(modele?.patternN, nb));
    setCale(!!modele?.cale);
    setSemaine1(modele?.semaine1 === 'impaire' ? 'impaire' : 'paire');
  }, [visible, modele]);

  // Redimensionne le motif à nb*7 (conserve l'existant, complète en parent1).
  function ajuster(pat, nb) {
    const out = [];
    for (let i = 0; i < nb * 7; i++) out.push((pat && pat[i]) || 'parent1');
    return out;
  }
  const changerNb = (nb) => { setNbSemaines(nb); setPattern(p => ajuster(p, nb)); };
  const basculer = (i) => setPattern(p => p.map((v, k) => (k === i ? (v === 'parent1' ? 'parent2' : 'parent1') : v)));

  const valide = nom.trim().length > 0 && pattern.length === nbSemaines * 7;
  // Disquette grisée tant que rien n'a changé (principe app-wide).
  const modifie = !modele
    ? true
    : (nom.trim() !== (modele.nom || '') || nbSemaines !== (modele.nbSemaines || 2)
       || JSON.stringify(pattern) !== JSON.stringify(modele.patternN || [])
       || cale !== !!modele.cale || (cale && nbSemaines === 2 && semaine1 !== (modele.semaine1 === 'impaire' ? 'impaire' : 'paire')));
  function enregistrer() {
    if (!valide || !modifie) return;
    onEnregistrer?.({ id: modele?.id || idPerso(), nom: nom.trim(), nbSemaines, patternN: pattern, cale, semaine1: cale ? semaine1 : null });
    onFermer?.();
  }

  const action = { icone: modele ? '💾' : '＋', onPress: enregistrer, disabled: !valide || !modifie, label: t('commun.enregistrer') };

  return (
    <Feuille visible={visible} titre={modele ? t('garde.modifierModele') : t('garde.modelePerso')} emoji="🧩" hauteur={0.82} onFermer={onFermer} action={action}>
      <Text style={s.lbl}>{t('listes.nom')}</Text>
      <TextInput style={s.input} value={nom} onChangeText={setNom} placeholder={t('garde.phModelePerso')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('garde.dureeCycle')}</Text>
      <ChoixPuces pleine value={nbSemaines} onChange={changerNb}
        options={[1, 2, 3, 4].map(n => ({ id: n, label: t('garde.nSem', { n }) }))} />

      <Text style={s.lbl}>{t('garde.calageCycle')}</Text>
      <ChoixPuces pleine value={cale} onChange={setCale}
        options={[{ id: false, label: t('garde.jourDepart') }, { id: true, label: t('garde.semainesCalendrier') }]} />
      {cale && nbSemaines === 2 && (
        <>
          <Text style={s.lbl}>{t('garde.s1CorrespondAux')}</Text>
          <ChoixPuces pleine value={semaine1} onChange={setSemaine1}
            options={[{ id: 'paire', label: t('garde.semainesPaires') }, { id: 'impaire', label: t('garde.semainesImpaires') }]} />
        </>
      )}

      <Text style={s.lbl}>{t('garde.repartitionToucheCase')}</Text>
      <View style={s.gEntete}>
        <Text style={[s.gJourLbl, { width: 46 }]} />
        {(cale ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['J1', 'J2', 'J3', 'J4', 'J5', 'J6', 'J7']).map((j, k) => <Text key={k} style={s.gJourLbl}>{j}</Text>)}
      </View>
      {Array.from({ length: nbSemaines }).map((_, w) => (
        <View key={w} style={s.gLigne}>
          <Text style={s.gSemLbl}>S{w + 1}</Text>
          {Array.from({ length: 7 }).map((_, d) => {
            const i = w * 7 + d; const g = pattern[i] || 'parent1'; const c = gardeCol[g];
            return (
              <TouchableOpacity key={d} style={[s.gCase, { backgroundColor: c + '22', borderColor: c }]} onPress={() => basculer(i)}>
                <Text style={[s.gCaseTxt, { color: c }]}>{g === 'parent1' ? 'P1' : 'P2'}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ))}

      <View style={s.legende}>
        {P.map(pp => (
          <View key={pp} style={s.legItem}>
            <View style={[s.legPuce, { backgroundColor: gardeCol[pp] }]} />
            <Text style={s.legTxt}>{pp === 'parent1' ? 'P1' : 'P2'} = {nomG(pp)}</Text>
          </View>
        ))}
      </View>
      <Text style={s.aide}>
        {cale
          ? (nbSemaines === 2
              ? t('garde.aideCaleDeux', { parite: semaine1 === 'impaire' ? t('garde.pariteImpaires') : t('garde.paritePaires') })
              : t('garde.aideCaleMulti', { n: nbSemaines }))
          : t('garde.aideNonCale')}
      </Text>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  semRow: { flexDirection: 'row', gap: 8 },
  semChip: { flex: 1, paddingVertical: 9, borderRadius: 10, borderWidth: 1, borderColor: UI.champBord, backgroundColor: UI.champ, alignItems: 'center' },
  semChipOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  semTxt: { fontSize: 13, fontWeight: '700', color: UI.ink },
  semTxtOn: { color: UI.accent, fontWeight: '800' },
  gEntete: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  gJourLbl: { flex: 1, textAlign: 'center', fontSize: 10.5, fontWeight: '700', color: UI.faint },
  gLigne: { flexDirection: 'row', alignItems: 'center', gap: 3, marginBottom: 4 },
  gSemLbl: { width: 46, fontSize: 12, fontWeight: '800', color: UI.muted },
  gCase: { flex: 1, aspectRatio: 1, borderRadius: 8, borderWidth: 1, alignItems: 'center', justifyContent: 'center', maxHeight: 42 },
  gCaseTxt: { fontSize: 11.5, fontWeight: '800' },
  legende: { flexDirection: 'row', gap: 16, marginTop: 12 },
  legItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legPuce: { width: 12, height: 12, borderRadius: 4 },
  legTxt: { fontSize: 12, color: UI.muted, fontWeight: '600' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 10, lineHeight: 16 },
});
